import { computed, unref } from 'vue';
import { useStore } from 'vuex';

import Course from '@/models/Course.js';
import CourseGroup from '@/models/CourseGroup.js';
import Role from '@/models/Role.js';
import Cache from '@/utilities/Cache.js';

/** Mirrors {@link AttendanceReportingRequest} and `Attendance::VIEW_ATTENDANCE_REPORTING_PERMISSION`. */
export const VIEW_ATTENDANCE_REPORTING_PERMISSION = 'view attendance reporting';

/**
 * @param {number|string|null|undefined} groupId
 * @returns {number|null}
 */
function normalizeGroupId(groupId) {
    const normalized = Number.parseInt(groupId, 10);

    return Number.isFinite(normalized) ? normalized : null;
}

/**
 * @param {Array<number|string|null|undefined>} groupIds
 * @returns {number[]}
 */
function normalizeGroupIds(groupIds) {
    return groupIds.map(normalizeGroupId).filter(id => id != null);
}

/**
 * @param {import('@/models/Course.js').default|number|string|null|undefined} courseOrId
 * @returns {number[]}
 */
function resolveCourseGroupIds(courseOrId) {
    const courseId = typeof courseOrId === 'object' && courseOrId != null ? Number(courseOrId.id) : Number(courseOrId);

    if (!Number.isFinite(courseId)) {
        return [];
    }

    if (typeof courseOrId === 'object' && courseOrId.groups?.length) {
        return normalizeGroupIds(courseOrId.groups.map(group => group.id));
    }

    if (typeof Course?.query === 'function') {
        const storedCourse = Course.query().with('groups').find(courseId);

        if (storedCourse?.groups?.length) {
            return normalizeGroupIds(storedCourse.groups.map(group => group.id));
        }
    }

    if (typeof CourseGroup?.query !== 'function') {
        return [];
    }

    return normalizeGroupIds(
        CourseGroup.query()
            .where('course_id', courseId)
            .get()
            .map(row => row.group_id)
    );
}

/**
 * @param {import('@/models/UserGroup.js').default|object} userGroup
 * @param {string} permission
 * @returns {boolean}
 */
function userGroupMembershipHasPermission(userGroup, permission) {
    if (!userGroup?.role_id) {
        return false;
    }

    const role =
        userGroup.role ??
        (typeof Role?.query === 'function' ? Role.query().with('permissions').find(userGroup.role_id) : null);

    return Boolean(role?.hasPermissionTo?.(permission));
}

/**
 * True when a failed course-group fetch might still leave reporting access unknown
 * (group Attendance Manager grants need course ↔ group intersection).
 *
 * @param {import('@/models/User.js').default|null|undefined} user
 * @returns {boolean}
 */
export function userMightNeedCourseGroupsForReporting(user) {
    if (!user || user.hasPermissionTo?.(VIEW_ATTENDANCE_REPORTING_PERMISSION)) {
        return false;
    }

    if (!Array.isArray(user.user_groups) || user.user_groups.length === 0) {
        return false;
    }

    return user.user_groups.some(userGroup =>
        userGroupMembershipHasPermission(userGroup, VIEW_ATTENDANCE_REPORTING_PERMISSION)
    );
}

/**
 * Same intersection as {@link User::hasPermissionViaGroupForCourse} without User#Cache,
 * so a first failed check before course groups hydrate cannot stick for the session.
 *
 * @param {import('@/models/User.js').default} user
 * @param {number[]|undefined} courseGroupIds
 * @param {string} permission
 * @returns {boolean}
 */
export function userHasViewReportingViaGroupForCourse(
    user,
    courseGroupIds,
    permission = VIEW_ATTENDANCE_REPORTING_PERMISSION
) {
    if (!courseGroupIds?.length || !Array.isArray(user.user_groups)) {
        return false;
    }

    const groupIdSet = new Set(normalizeGroupIds(courseGroupIds));

    return user.user_groups.some(userGroup => {
        const groupId = normalizeGroupId(userGroup.group_id);

        return groupId != null && groupIdSet.has(groupId) && userGroupMembershipHasPermission(userGroup, permission);
    });
}

/**
 * Whether the user's enrollment in the course carries the permission, through its role or a direct grant.
 *
 * @param {import('@/models/User.js').default} user
 * @param {number|string|null|undefined} courseId
 * @param {string} permission
 * @returns {boolean}
 */
export function userHasViewReportingViaEnrollmentForCourse(
    user,
    courseId,
    permission = VIEW_ATTENDANCE_REPORTING_PERMISSION
) {
    const normalizedCourseId = Number.parseInt(courseId, 10);

    if (!Number.isFinite(normalizedCourseId)) {
        return false;
    }

    return Boolean(user.isEnrolledWithPermission?.({ id: normalizedCourseId }, permission));
}

/**
 * Whether the user may read the Admin Story 10 clock-hour report for a course.
 * Global CASL `$can` only reflects site-wide roles; group- and enrollment-scoped
 * Attendance Manager grants are resolved here, matching the API gate.
 *
 * Deliberately not {@link User#hasPermissionViaCourseTo}: that passes every course permission
 * for a group membership with no role, while AttendanceReportingRequest only accepts a course
 * group membership or enrollment whose role carries the reporting permission.
 *
 * @param {import('@/models/User.js').default|null|undefined} user
 * @param {import('@/models/Course.js').default|number|string|null|undefined} courseOrId
 * @returns {boolean}
 */
export function userCanViewAttendanceReporting(user, courseOrId) {
    if (!user) {
        return false;
    }

    if (user.hasPermissionTo?.(VIEW_ATTENDANCE_REPORTING_PERMISSION)) {
        return true;
    }

    const courseId = typeof courseOrId === 'object' && courseOrId != null ? courseOrId.id : courseOrId;

    if (courseId == null || courseId === '') {
        return false;
    }

    const courseGroupIds = resolveCourseGroupIds(courseOrId);

    if (userHasViewReportingViaGroupForCourse(user, courseGroupIds)) {
        return true;
    }

    return userHasViewReportingViaEnrollmentForCourse(user, courseId);
}

/**
 * Drop cached course-scoped permission results for one course after its groups are hydrated.
 *
 * @param {number|string|null|undefined} userId
 * @param {number|string|null|undefined} courseId
 */
export function forgetCachedCourseReportingPermission(userId, courseId) {
    if (userId == null || courseId == null || courseId === '') {
        return;
    }

    const normalizedCourseId = Number(courseId);

    if (!Number.isFinite(normalizedCourseId)) {
        return;
    }

    Cache.forgetPrefix(
        `user:${userId}:has-group-permission-${VIEW_ATTENDANCE_REPORTING_PERMISSION}-within-course:${normalizedCourseId}`
    );
    Cache.forgetPrefix(`user:${userId}:has-limited-scope-permission-via-course-to:${normalizedCourseId}:`);
    Cache.forgetPrefix(`user:${userId}:is-enrolled-with-permission:${normalizedCourseId}:`);
}

/**
 * @param {import('vue').MaybeRefOrGetter<import('@/models/Course.js').default|number|string|null|undefined>} courseOrId
 */
export function useCanViewAttendanceReporting(courseOrId) {
    const store = useStore();

    return computed(() => userCanViewAttendanceReporting(store.getters['Auth/user'], unref(courseOrId)));
}
